import React, { useState, useEffect } from 'react';
import { IoClose, IoSaveOutline, IoDocumentText, IoTrashOutline } from 'react-icons/io5';
import { db } from '../firebaseConfig';
import { collection, addDoc, Timestamp, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { postLedgerEntry, getAccountMap, ACCOUNTS } from '../utils/accountingUtils';
import { DEFAULT_CHART_OF_ACCOUNTS } from '../utils/accounting/constants';
import moment from 'moment';
import './ClasificarGastoModal.css'; // Reusing styles

const RegistrarGastoModal = ({ onClose, onSuccess, expenseToEdit, onRequestCreateProvider }) => {
    const { userData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetchingAccounts, setFetchingAccounts] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [providerList, setProviderList] = useState([]);
    const [modalError, setModalError] = useState(null);

    // Form State - Initialize from editing object if present
    const [amount, setAmount] = useState(expenseToEdit ? expenseToEdit.monto : '');
    const [provider, setProvider] = useState(expenseToEdit ? expenseToEdit.proveedor : '');
    const [description, setDescription] = useState(expenseToEdit ? expenseToEdit.descripcion : '');
    const [date, setDate] = useState(expenseToEdit?.fecha ? moment(expenseToEdit.fecha.toDate()).format('YYYY-MM-DD') : new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(expenseToEdit?.fechaVencimiento ? moment(expenseToEdit.fechaVencimiento.toDate()).format('YYYY-MM-DD') : new Date().toISOString().split('T')[0]);

    // Split accounts & taxes
    const [iva, setIva] = useState(expenseToEdit ? (expenseToEdit.iva || '') : '');
    const [distribucion, setDistribucion] = useState(() => {
        if (expenseToEdit && expenseToEdit.distribucion) {
            return expenseToEdit.distribucion.map(d => ({
                cuentaCodigo: d.cuentaCodigo || '',
                subtotal: d.subtotal || '',
                searchTerm: d.cuentaCodigo ? `${d.cuentaCodigo}` : '',
                showSuggestions: false
            }));
        }
        return [{ cuentaCodigo: expenseToEdit?.cuentaCodigo || '', subtotal: expenseToEdit?.monto || '', searchTerm: expenseToEdit?.cuentaCodigo || '', showSuggestions: false }];
    });

    useEffect(() => {
        const subtotalSum = distribucion.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
        const total = subtotalSum + (parseFloat(iva) || 0);
        setAmount(total > 0 ? total.toFixed(2) : '');
    }, [distribucion, iva]);

    useEffect(() => {
        if (userData?.condominioId) {
            fetchAccounts();
            fetchProviders();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchProviders = async () => {
        try {
            const q = query(
                collection(db, "condominio_proveedores"),
                where("condominioId", "==", userData.condominioId),
                orderBy("nombre", "asc")
            );
            const snap = await getDocs(q);
            setProviderList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error("Error loading providers:", e);
        }
    };

    const fetchAccounts = async () => {
        try {
            const q = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), orderBy("numero", "asc"));
            const snap = await getDocs(q);
            let fetched = [];

            if (!snap.empty) {
                fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                fetched = fetched.filter(a => ['GASTO', 'EGRESO', 'ACTIVO', 'PASIVO', 'COSTO'].includes(a.tipo) || (a.codigo && (a.codigo.toString().startsWith('5') || a.codigo.toString().startsWith('2'))));
            } else {
                fetched = DEFAULT_CHART_OF_ACCOUNTS
                    .filter(a => ['GASTO', 'ACTIVO', 'PASIVO', 'COSTO'].includes(a.tipo))
                    .map(a => ({ ...a, id: `DEF_${a.codigo}`, numero: a.codigo }));
            }
            setAccounts(fetched);
        } catch (error) {
            console.error("Error fetching accounts:", error);
        } finally {
            setFetchingAccounts(false);
        }
    };

    const handleAddSplitLine = () => {
        setDistribucion(prev => [
            ...prev,
            { cuentaCodigo: '', subtotal: '', searchTerm: '', showSuggestions: false }
        ]);
    };

    const handleRemoveSplitLine = (idx) => {
        setDistribucion(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSplitSearchChange = (idx, val) => {
        setDistribucion(prev => {
            const copy = [...prev];
            copy[idx].searchTerm = val;
            copy[idx].showSuggestions = true;
            return copy;
        });
    };

    const handleSelectSplitAccount = (idx, acc) => {
        setDistribucion(prev => {
            const copy = [...prev];
            copy[idx].cuentaCodigo = acc.codigo || acc.numero;
            copy[idx].searchTerm = `${acc.codigo || acc.numero} - ${acc.nombre}`;
            copy[idx].showSuggestions = false;
            return copy;
        });
    };

    const handleSplitSubtotalChange = (idx, val) => {
        setDistribucion(prev => {
            const copy = [...prev];
            copy[idx].subtotal = val;
            return copy;
        });
    };

    const handleSave = async () => {
        const validLines = distribucion.filter(l => l.cuentaCodigo && l.subtotal);
        if (validLines.length === 0) {
            setModalError("Por favor selecciona al menos una Cuenta Contable con su monto.");
            return;
        }

        if (!provider || !description) {
            setModalError("Por favor completa el proveedor y la descripción.");
            return;
        }

        setLoading(true);
        try {
            const numericAmount = parseFloat(amount);
            const expenseDate = Timestamp.fromDate(new Date(date));
            const expenseDueDate = Timestamp.fromDate(new Date(dueDate));

            const firstLineAcc = accounts.find(a => a.codigo === validLines[0].cuentaCodigo || a.numero === validLines[0].cuentaCodigo);
            const resolvedAccountId = firstLineAcc ? firstLineAcc.id : '';
            const resolvedAccountName = firstLineAcc ? `${firstLineAcc.numero || firstLineAcc.codigo} - ${firstLineAcc.nombre}` : '';

            // 1. Create or Update Gasto Document (Provision)
            let gastoRef;
            const data = {
                condominioId: userData.condominioId,
                monto: numericAmount,
                subtotal: distribucion.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0),
                iva: parseFloat(iva || 0),
                distribucion: validLines.map(l => ({
                    cuentaCodigo: l.cuentaCodigo,
                    subtotal: parseFloat(l.subtotal),
                    cuentaNombre: accounts.find(a => a.codigo === l.cuentaCodigo || a.numero === l.cuentaCodigo)?.nombre || ''
                })),
                tipo: 'egreso',
                estatus: expenseToEdit ? expenseToEdit.estatus : 'pendiente',
                fecha: expenseDate,
                fechaVencimiento: expenseDueDate,
                ...(expenseToEdit ? {} : { fechaRegistro: Timestamp.now() }),

                // Standardized Accounting Fields
                cuentaId: resolvedAccountId,
                cuentaContableId: resolvedAccountId, // Keep for legacy compatibility
                categoria: resolvedAccountName, // Keep for legacy compatibility
                cuentaNombre: resolvedAccountName,
                cuentaCodigo: validLines[0].cuentaCodigo,

                descripcion: description,
                proveedor: provider,
                registradoPor: userData.uid || 'admin',
                provisionado: true
            };

            if (expenseToEdit) {
                gastoRef = doc(db, 'gastos', expenseToEdit.id);
                await updateDoc(gastoRef, data);
            } else {
                gastoRef = await addDoc(collection(db, 'gastos'), data);
            }

            // 2. Post Ledger Entry (Provision)
            try {
                if (expenseToEdit) {
                    const qLedger = query(collection(db, "contabilidad_asientos"), where("referenciaId", "==", expenseToEdit.id));
                    const ledgerSnap = await getDocs(qLedger);
                    for (const d of ledgerSnap.docs) {
                        await deleteDoc(d.ref);
                    }
                }

                const accountMap = await getAccountMap(userData.condominioId);
                const apAccountId = accountMap[ACCOUNTS.PROVEEDORES];

                if (apAccountId) {
                    const detalles = [];
                    let totalDebit = 0;
                    for (const row of data.distribucion) {
                        const subAcc = accounts.find(a => a.codigo === row.cuentaCodigo || a.numero === row.cuentaCodigo);
                        if (subAcc) {
                            detalles.push({ cuentaId: subAcc.id, debe: row.subtotal, haber: 0 });
                            totalDebit += row.subtotal;
                        }
                    }
                    if (data.iva > 0) {
                        let ivaAcc = accounts.find(a => (a.codigo === '1104' || a.numero === '1104' || a.codigo === '1106' || a.numero === '1106'));
                        if (!ivaAcc) {
                            ivaAcc = accounts.find(a => a.nombre.toLowerCase().includes('iva acreditable') || a.nombre.toLowerCase().includes('iva por acreditar'));
                        }
                        if (ivaAcc) {
                            detalles.push({ cuentaId: ivaAcc.id, debe: data.iva, haber: 0 });
                            totalDebit += data.iva;
                        }
                    }
                    detalles.push({ cuentaId: apAccountId, debe: 0, haber: totalDebit });

                    await postLedgerEntry(userData.condominioId, {
                        fecha: expenseDate,
                        concepto: `Provisión Gasto: ${provider} - ${description}`,
                        referenciaId: gastoRef.id,
                        tipoReferencia: 'GASTO_PROVISION',
                        detalles: detalles
                    });
                } else {
                    console.warn("Cuenta de Proveedores (2000) no encontrada. No se generó asiento contable.");
                }

            } catch (ledgerError) {
                console.error("Error posting ledger:", ledgerError);
            }

            if (onSuccess) onSuccess();
            onClose();

        } catch (error) {
            console.error("Error creating provision:", error);
            setModalError("Error al guardar el gasto: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '540px', width: '90%', borderRadius: '16px' }}>
                <div className="modal-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
                    <h3><IoDocumentText /> Registrar Factura (Gasto)</h3>
                    <button onClick={onClose} className="close-btn"><IoClose /></button>
                </div>

                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 0' }}>
                    {modalError && (
                        <div style={{
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '10px',
                            color: '#b91c1c',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontWeight: '600'
                        }}>
                            <span>{modalError}</span>
                            <button
                                onClick={() => setModalError(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#b91c1c',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '1.1em'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                    )}
                    <div className="form-group" style={{ position: 'relative', marginBottom: '16px' }}>
                        <label>Proveedor *</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={provider}
                            onChange={e => setProvider(e.target.value)}
                            placeholder="Buscar o escribir nombre..."
                            autoComplete="off"
                        />
                        {provider && !providerList.some(p => p.nombre.toLowerCase() === provider.toLowerCase()) && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                background: 'white', border: '1px solid #ddd',
                                borderRadius: '0 0 8px 8px', zIndex: 10,
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                            }}>
                                <div
                                    style={{ padding: '10px', color: '#dc2626', fontSize: '0.9em', cursor: 'pointer', background: '#fef2f2' }}
                                    onClick={() => {
                                        if (onRequestCreateProvider) {
                                            onRequestCreateProvider(provider);
                                        } else {
                                            alert("Función de crear proveedor no disponible aquí.");
                                        }
                                    }}
                                >
                                    <strong>+ Crear "{provider}"</strong>
                                    <br /><span style={{ fontSize: '0.8em', color: '#666' }}>No existe. Click para registrarlo completo.</span>
                                </div>
                            </div>
                        )}

                        {providerList.length > 0 && provider && providerList.some(p => p.nombre.toLowerCase().includes(provider.toLowerCase())) && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                background: 'white', border: '1px solid #ddd',
                                maxHeight: '150px', overflowY: 'auto', zIndex: 10
                            }}>
                                {providerList.filter(p => p.nombre.toLowerCase().includes(provider.toLowerCase())).map(p => (
                                    <div
                                        key={p.id}
                                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                        onClick={() => setProvider(p.nombre)}
                                        className="dropdown-item"
                                    >
                                        {p.nombre}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Dynamic Concept and Split lines */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{ fontWeight: '700', fontSize: '0.9em', color: '#334155' }}>Conceptos y Distribución de Gasto</span>
                            <button
                                type="button"
                                onClick={handleAddSplitLine}
                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 8px', fontSize: '0.8em', fontWeight: 'bold', color: '#475569', cursor: 'pointer' }}
                            >
                                + Agregar Cuenta
                            </button>
                        </div>

                        {distribucion.map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', position: 'relative' }}>
                                <div style={{ flex: 2, position: 'relative' }}>
                                    <label style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700', display: 'block', marginBottom: '4px' }}>Cuenta Contable</label>
                                    <input
                                        type="text"
                                        className="modal-input"
                                        style={{ padding: '6px 10px', fontSize: '0.88em' }}
                                        placeholder="Buscar cuenta..."
                                        value={item.searchTerm || ''}
                                        onChange={(e) => handleSplitSearchChange(idx, e.target.value)}
                                        onFocus={() => setDistribucion(prev => {
                                            const copy = [...prev];
                                            copy[idx].showSuggestions = true;
                                            return copy;
                                        })}
                                        onBlur={() => setTimeout(() => {
                                            setDistribucion(prev => {
                                                const copy = [...prev];
                                                if (copy[idx]) copy[idx].showSuggestions = false;
                                                return copy;
                                            });
                                        }, 200)}
                                    />
                                    {item.showSuggestions && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            background: 'white',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '0 0 8px 8px',
                                            marginTop: '4px',
                                            maxHeight: '150px',
                                            overflowY: 'auto',
                                            zIndex: 60 + idx,
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {accounts
                                                .filter(a => {
                                                    const s = (item.searchTerm || '').toLowerCase();
                                                    return !s || 
                                                        (a.nombre && a.nombre.toLowerCase().includes(s)) || 
                                                        (a.codigo && a.codigo.toString().includes(s)) || 
                                                        (a.numero && a.numero.toString().includes(s));
                                                })
                                                .map(a => (
                                                    <div
                                                        key={a.id}
                                                        style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.85em' }}
                                                        onMouseDown={() => handleSelectSplitAccount(idx, a)}
                                                    >
                                                        <strong>{a.codigo || a.numero}</strong> - {a.nombre}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700', display: 'block', marginBottom: '4px' }}>Monto</label>
                                    <input
                                        type="number"
                                        className="modal-input"
                                        style={{ padding: '6px 10px', fontSize: '0.88em' }}
                                        placeholder="$0.00"
                                        value={item.subtotal || ''}
                                        onChange={(e) => handleSplitSubtotalChange(idx, e.target.value)}
                                    />
                                </div>

                                {distribucion.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSplitLine(idx)}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', marginTop: '22px', cursor: 'pointer', padding: '4px' }}
                                        title="Eliminar línea"
                                    >
                                        <IoTrashOutline size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div className="form-group">
                            <label>Fecha Factura *</label>
                            <input
                                type="date"
                                className="modal-input"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>IVA (Impuestos)</label>
                            <input
                                type="number"
                                className="modal-input"
                                placeholder="$0.00"
                                value={iva}
                                onChange={e => setIva(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div className="form-group">
                            <label>Vencimiento</label>
                            <input
                                type="date"
                                className="modal-input"
                                value={dueDate}
                                onChange={e => setDueDate(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Monto Total (Autocalculado)</label>
                            <input
                                type="text"
                                className="modal-input"
                                style={{ background: '#f1f5f9', fontWeight: 'bold' }}
                                value={amount ? `$${parseFloat(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '$0.00'}
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '16px' }}>
                        <label>Descripción *</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Concepto del gasto"
                        />
                    </div>
                </div>

                <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', marginTop: '10px' }}>
                    <button className="secondary-btn" onClick={onClose}>Cancelar</button>
                    <button className="primary-btn" onClick={handleSave} disabled={loading}>
                        {loading ? 'Guardando...' : <><IoSaveOutline /> Guardar Provisión</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegistrarGastoModal;
